import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  DndContext,
  closestCenter,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ReactComponent as GaleryIcon } from "../../assets/newImages/icons/galery.svg";
import { ReactComponent as PlayIcon } from "../../assets/newImages/icons/play.svg";
import { CatalogoContext } from "../../contexts/Catalogo/CatalogoContext";
import { useTheme } from "../../contexts/Theme/ThemeContext";
import {
  BannerArte,
  BannerCatalogo,
  BannerDestino,
  OpcoesDirecionamento,
} from "../../types/Catalogo";
import { CatalogosApi } from "../../hooks/catalogos";
import { Select } from "../Select";
import { Toggle } from "../Toggle/Toggle";
import { Button } from "../Button";
import { boolean } from "../../services/Functions";
import PopoverInfo from "../PopoverInfo/PopoverInfo";
import Swal from "sweetalert2";
import LockedIcon from "../LockedIcon/LockedIcon";
import { ImageUploadWithCrop } from "../Upload/ImageUploadWithCrop";
import Modal from "../Modal/Modal";
import { ModalCropContainer } from "../Upload/imageUploadProduto/styles";
import { Controls, Player } from "@lottiefiles/react-lottie-player";
import LoadingSRC from "../../assets/jsons/Loading.json";
import TableMainContainer from "../Table/styles";
import "./styles";
import { BannersShowcaseContainer } from "./styles";

interface BannersShowcaseProps {
  quantidade_permitida?: number;
  disabled?: boolean;
}

const DESKTOP = {
  aspectRatio: 8 / 1,
  minWidth: 1200,
  minHeight: 150,
  resize: { width: 1400, height: 175 },
};

const MOBILE = {
  aspectRatio: 2 / 1,
  minWidth: 640,
  minHeight: 320,
  resize: { width: 640, height: 320 },
};

const BANNER_VAZIO: BannerCatalogo = {
  imagem_desktop: null,
  imagem_mobile: null,
  ativo: true,
  destino: { tipo: "nenhum" },
};

const urlDaArte = (arte: BannerArte | null): string =>
  !arte ? "" : typeof arte === "string" ? arte : arte.base64;

// O valor do ajuste chega sem máscara (só dígitos). "", "0" e "000" significam
// que o cliente ligou o toggle mas ainda não digitou um desconto de verdade.
const temValorDeAjuste = (valor?: string) => {
  const digitos = String(valor ?? "").replace(/\D/g, "");
  return digitos !== "" && Number(digitos) > 0;
};

// Mesma alça de arraste do modal de ordenação de categorias.
const AlcaDeArraste: React.FC<{ cor: string }> = ({ cor }) => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="7" cy="5" r="1.5" fill={cor} />
    <circle cx="13" cy="5" r="1.5" fill={cor} />
    <circle cx="7" cy="10" r="1.5" fill={cor} />
    <circle cx="13" cy="10" r="1.5" fill={cor} />
    <circle cx="7" cy="15" r="1.5" fill={cor} />
    <circle cx="13" cy="15" r="1.5" fill={cor} />
  </svg>
);

interface LinhaBannerProps {
  id: string;
  aberto: boolean;
  disabled: boolean;
  capa: string;
  destino: string;
  ativo: boolean;
  onAbrir: () => void;
  onAlternarAtivo: () => void;
}

const LinhaBanner: React.FC<LinhaBannerProps> = ({
  id,
  aberto,
  disabled,
  capa,
  destino,
  ativo,
  onAbrir,
  onAlternarAtivo,
}) => {
  const { theme } = useTheme();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled,
  });

  return (
    <tr
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
      }}
      className={aberto ? "aberto" : ""}
    >
      <td>
        <div className="banner-alca" {...attributes} {...listeners}>
          <AlcaDeArraste cor={theme.colors.tertiary} />
        </div>
      </td>

      <td className="banner-midia" onClick={() => !disabled && onAbrir()}>
        {capa ? (
          <img src={capa} alt="Banner" />
        ) : (
          <span className="banner-sem-arte">Sem arte</span>
        )}
      </td>

      <td className="banner-direcionamento fonteTexto" onClick={() => !disabled && onAbrir()}>
        {destino}
      </td>

      <td>
        <div className="banner-status">
          <Toggle
            value={ativo}
            disabled={disabled}
            onChange={onAlternarAtivo}
            ignoreValidations={true}
          />
        </div>
      </td>
    </tr>
  );
};

const BannersShowcase: React.FC<BannersShowcaseProps> = ({
  quantidade_permitida = 0,
  disabled = false,
}) => {
  const { theme } = useTheme();
  const { catalogo, setCatalogo, ajustesCategoria, ajustesVariacao } =
    useContext(CatalogoContext);

  const [loading, setLoading] = useState<boolean>(false);
  const [erro, setErro] = useState<string>("");
  const [arrastando, setArrastando] = useState<string>("");

  // Só o que existe DENTRO do catálogo pode virar destino: apontar o banner para
  // uma categoria sem produto aqui levaria o cliente a uma lista vazia.
  const [opcoes, setOpcoes] = useState<OpcoesDirecionamento>({
    categorias: [],
    tags: [],
    variacoes: [],
  });
  // Só bloqueamos categoria/tag/variação depois que a busca das opções retornou;
  // antes disso ainda não dá para dizer que estão vazias.
  const [carregouOpcoes, setCarregouOpcoes] = useState(false);

  // O banner aberto é editado direto na lista do catálogo. O rascunho (banner sem
  // nenhuma arte) fica sempre aberto no formulário e não aparece na tabela.
  const [editando, setEditando] = useState<number | null>(null);

  const todosProdutos = Boolean(catalogo.configuracoes?.adicionarTodosProdutos);
  const produtosDoCatalogo = catalogo.produtos ?? [];
  // A lista de ids muda de identidade a cada render; a chave é o que de fato importa.
  const chaveProdutos = todosProdutos ? "todos" : produtosDoCatalogo.join(",");

  useEffect(() => {
    let cancelado = false;

    // Um destino só é válido enquanto o que ele aponta ainda existe no catálogo.
    // 'nenhum' e 'promocao' não dependem dos produtos; os demais, sim. Quando a
    // categoria/tag/variação vinculada sai do catálogo, o banner volta a ficar sem
    // direcionamento em vez de apontar para uma lista que não existe mais.
    const sanitizarDestinos = (opcoesAtuais: OpcoesDirecionamento) => {
      const valido = (destino: BannerDestino): boolean => {
        switch (destino.tipo) {
          case "categoria":
            return opcoesAtuais.categorias.some((c) =>
              destino.subcategoria
                ? c.categoria === destino.categoria &&
                  c.subcategorias.includes(destino.subcategoria)
                : c.categoria === destino.categoria
            );
          case "tag":
            return opcoesAtuais.tags.includes(destino.tag);
          case "variacao":
            return opcoesAtuais.variacoes.includes(destino.variacao);
          default:
            return true;
        }
      };

      setCatalogo((anterior) => {
        let mudou = false;
        const banners = anterior.banners.map((banner) => {
          if (valido(banner.destino)) return banner;
          mudou = true;
          return { ...banner, destino: { tipo: "nenhum" as const } };
        });
        return mudou ? { ...anterior, banners } : anterior;
      });
    };

    CatalogosApi()
      .getOpcoesDirecionamento(produtosDoCatalogo, todosProdutos)
      .then((resposta) => {
        if (cancelado) return;
        setOpcoes(resposta);
        // Só limpa direcionamentos quando a busca deu certo: numa falha de rede as
        // opções voltam vazias e apagariam destinos válidos sem motivo.
        sanitizarDestinos(resposta);
      })
      .catch(() => {
        if (!cancelado) setOpcoes({ categorias: [], tags: [], variacoes: [] });
      })
      .finally(() => {
        if (!cancelado) setCarregouOpcoes(true);
      });

    return () => {
      cancelado = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chaveProdutos, todosProdutos]);

  // O destino "produtos em promoção" só faz sentido enquanto a promoção do
  // catálogo (ajuste de preço) estiver ligada — sem ela o banner levaria a uma
  // lista vazia. Se o cliente desliga a promoção, o banner volta a ficar sem
  // direcionamento, do mesmo jeito que uma categoria removida.
  useEffect(() => {
    if (catalogo.ajuste_preco_ativo) return;

    setCatalogo((anterior) => {
      let mudou = false;
      const banners = anterior.banners.map((banner) => {
        if (banner.destino.tipo !== "promocao") return banner;
        mudou = true;
        return { ...banner, destino: { tipo: "nenhum" as const } };
      });
      return mudou ? { ...anterior, banners } : anterior;
    });
  }, [catalogo.ajuste_preco_ativo, setCatalogo]);

  const categoriasPai = useMemo(
    () => opcoes.categorias.map((categoria) => categoria.categoria),
    [opcoes]
  );

  // Um select só, igual ao do produto: a categoria e cada "Categoria / Subcategoria"
  // viram uma linha da mesma lista.
  const opcoesCategoria = useMemo(() => {
    const lista: { categoria: string; subcategoria?: string; label: string }[] = [];
    opcoes.categorias.forEach((categoria) => {
      lista.push({ categoria: categoria.categoria, label: categoria.categoria });
      categoria.subcategorias.forEach((sub) => {
        lista.push({
          categoria: categoria.categoria,
          subcategoria: sub,
          label: `${categoria.categoria} / ${sub}`,
        });
      });
    });
    return lista;
  }, [opcoes]);

  const banners = catalogo.banners;
  const bannerAberto = editando !== null ? banners[editando] : undefined;

  // Banner sem nenhuma arte é rascunho: fica no formulário, não na tabela.
  const temArte = (banner: BannerCatalogo) =>
    Boolean(banner.imagem_desktop || banner.imagem_mobile);

  // Só os banners com arte contam para a tabela e para o limite; o rascunho aberto
  // não ocupa vaga. Guardamos o índice original para editar/reordenar/desligar.
  const cadastrados = banners
    .map((banner, index) => ({ banner, index }))
    .filter(({ banner }) => temArte(banner));

  const noLimite = quantidade_permitida > 0 && cadastrados.length >= quantidade_permitida;

  const descreverDestino = (destino: BannerDestino): string => {
    switch (destino.tipo) {
      case "categoria":
        return destino.subcategoria
          ? `Categoria: ${destino.categoria} / ${destino.subcategoria}`
          : `Categoria: ${destino.categoria}`;
      case "tag":
        return `Tag de destaque: ${destino.tag}`;
      case "variacao":
        return `Variação: ${destino.variacao}`;
      case "promocao":
        return "Produtos em promoção";
      default:
        return "Sem direcionamento";
    }
  };

  // "Produtos em promoção" só existe quando há um Ajuste de Preço ativo que REDUZ
  // o valor E com a porcentagem/valor já preenchido — só ligar o toggle não basta.
  const temPromocao = useMemo(() => {
    if (!boolean(catalogo.ajuste_preco_ativo)) return false;
    const escopo = catalogo.ajuste_preco_escopo || "catalogo";
    if (escopo === "catalogo") {
      return (
        catalogo.ajuste_preco_direcao === "diminuir" &&
        temValorDeAjuste(catalogo.ajuste_preco_valor)
      );
    }
    if (escopo === "categoria") {
      return ajustesCategoria.some(
        (a) =>
          a.ativo &&
          a.ajuste_direcao === "diminuir" &&
          (a.qtd_produtos || 0) > 0 &&
          temValorDeAjuste(a.ajuste_valor)
      );
    }
    return ajustesVariacao.some(
      (a) =>
        a.ativo &&
        a.ajuste_direcao === "diminuir" &&
        a.produtos.length > 0 &&
        temValorDeAjuste(a.ajuste_valor)
    );
  }, [
    catalogo.ajuste_preco_ativo,
    catalogo.ajuste_preco_escopo,
    catalogo.ajuste_preco_direcao,
    catalogo.ajuste_preco_valor,
    ajustesCategoria,
    ajustesVariacao,
  ]);

  const atualizarBanners = (proximos: BannerCatalogo[]) => {
    setCatalogo((anterior) => ({ ...anterior, banners: proximos }));
  };

  // Toda edição do formulário cai aqui: escreve direto no banner da lista.
  const alterarBannerAberto = (mudanca: Partial<BannerCatalogo>) => {
    if (editando === null) return;

    setErro("");
    setCatalogo((anterior) => ({
      ...anterior,
      banners: anterior.banners.map((banner, i) =>
        i === editando ? { ...banner, ...mudanca } : banner
      ),
    }));
  };

  const alternarAtivo = (index: number) => {
    setErro("");

    // Desligar é sempre permitido; ligar não, porque banner que vai para a loja
    // precisa das duas artes — é a mesma regra do "Salvar banner".
    const alvo = banners[index];
    if (alvo && !alvo.ativo && !(alvo.imagem_desktop && alvo.imagem_mobile)) {
      Swal.fire({
        title: "Arte obrigatória",
        text: alvo.imagem_desktop
          ? "Para ativar o banner, envie também a arte para celulares (640 x 320px)."
          : "Para ativar o banner, envie também a arte para computadores (1400 x 175px).",
        icon: "warning",
        confirmButtonText: "Ok",
      });
      return;
    }

    atualizarBanners(
      banners.map((atual, i) => (i === index ? { ...atual, ativo: !atual.ativo } : atual))
    );
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const aoSoltar = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const de = Number(active.id);
    const para = Number(over.id);
    atualizarBanners(arrayMove(banners, de, para));

    // O formulário aberto acompanha o banner que estava sendo editado.
    if (editando === null) return;
    if (editando === de) setEditando(para);
    else if (de < editando && para >= editando) setEditando(editando - 1);
    else if (de > editando && para <= editando) setEditando(editando + 1);
  };

  // Abre um formulário novo em branco, descartando qualquer rascunho anterior sem
  // arte para não acumular banners vazios.
  const abrirNovo = () => {
    setErro("");
    const limpos = banners.filter(temArte);
    setEditando(limpos.length);
    atualizarBanners([...limpos, { ...BANNER_VAZIO }]);
  };

  // Sem botão "Adicionar": a tela já abre em modo de cadastro. Sempre que não há
  // nada sendo editado e ainda cabe banner, um formulário novo abre sozinho.
  useEffect(() => {
    if (!disabled && editando === null && !noLimite) {
      abrirNovo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled, editando, noLimite]);

  const abrirEdicao = (index: number) => {
    setErro("");
    const alvo = banners[index];
    // Descarta o rascunho em branco ao trocar para editar um banner já existente.
    const limpos = banners.filter(temArte);
    if (limpos.length !== banners.length) {
      atualizarBanners(limpos);
    }
    setEditando(limpos.indexOf(alvo));
  };

  const concluirEdicao = () => {
    if (editando === null || !bannerAberto) return;

    // O banner ATIVO precisa das duas artes: se tem a de um formato, a do outro é
    // obrigatória — a loja escolhe a versão conforme o dispositivo do cliente.
    // Inativo não vai para a loja, então pode ficar incompleto até ser ligado.
    const temDesktop = Boolean(bannerAberto.imagem_desktop);
    const temMobile = Boolean(bannerAberto.imagem_mobile);
    if (bannerAberto.ativo && (!temDesktop || !temMobile)) {
      const texto =
        !temDesktop && !temMobile
          ? "Envie a arte para computadores (1400 x 175px) e para celulares (640 x 320px)."
          : temDesktop
          ? "Você enviou a arte para computadores. Envie também a de celulares (640 x 320px)."
          : "Você enviou a arte para celulares. Envie também a de computadores (1400 x 175px).";
      Swal.fire({
        title: "Arte obrigatória",
        text: texto,
        icon: "warning",
        confirmButtonText: "Ok",
      });
      return;
    }

    setEditando(null);
    setErro("");
  };

  const definirDestino = (tipo: BannerDestino["tipo"]) => {
    switch (tipo) {
      case "categoria":
        alterarBannerAberto({
          destino: { tipo, categoria: categoriasPai[0] ?? "" },
        });
        break;
      case "tag":
        alterarBannerAberto({ destino: { tipo, tag: opcoes.tags[0] ?? "" } });
        break;
      case "variacao":
        alterarBannerAberto({ destino: { tipo, variacao: opcoes.variacoes[0] ?? "" } });
        break;
      case "promocao":
        // Promoção não tem valor: a loja filtra os produtos com desconto sozinha.
        alterarBannerAberto({ destino: { tipo: "promocao" } });
        break;
      default:
        alterarBannerAberto({ destino: { tipo: "nenhum" } });
    }
  };

  const modal = (cropping: JSX.Element, cancelar: JSX.Element, salvar: JSX.Element) => (
    <Modal isOpen={true} setModal={() => {}} width={"auto"} borderRadius="20px">
      <ModalCropContainer theme={theme}>
        <div className="titulo">Editar imagem</div>
        {cropping}
        <div className="botoes">
          {cancelar}
          {salvar}
        </div>
      </ModalCropContainer>
    </Modal>
  );

  const upload = (
    campo: "imagem_desktop" | "imagem_mobile",
    medidas: typeof DESKTOP,
    titulo: string,
    recomendado: string
  ) => {
    const arte = bannerAberto?.[campo] ?? null;
    const puxando = arrastando === campo;

    return (
      <div className="banner-arte">
        <div className="banner-arte-titulo">
          <span className="fonteTexto">{titulo}</span>
          <small className="fonteRotulos">Tamanho recomendado {recomendado}</small>
        </div>

        <ImageUploadWithCrop
          className="banner-upload"
          onLoading={setLoading}
          acceptFormats=".png, .jpeg, .jpg, .webp, .heic, .heif"
          value=""
          aspectRatio={medidas.aspectRatio}
          onChange={(base64: string, imagem: string, size: number) =>
            alterarBannerAberto({ [campo]: { base64, imagem, size: size.toString() } })
          }
          resize={medidas.resize}
          errorMargin={5}
          minWidth={medidas.minWidth}
          minHeight={medidas.minHeight}
          onOpenCrop={modal}
          onDragIn={() => setArrastando(campo)}
          onDragOut={() => setArrastando("")}
          onDragDrop={() => setArrastando("")}
        >
          <div className="banner-dropzone">
            {loading ? (
              <Player autoplay loop src={LoadingSRC} style={{ height: "3rem", width: "3rem" }}>
                <Controls visible={false} buttons={["play", "repeat", "frame", "debug"]} />
              </Player>
            ) : arte ? (
              <img src={urlDaArte(arte)} alt={titulo} />
            ) : (
              <>
                <div className="icones">
                  <GaleryIcon className={`galery-icon ${puxando ? "galery-ativo" : ""}`} />
                  <PlayIcon className={`play-icon ${puxando ? "play-ativo" : ""}`} />
                </div>
                <div className="texto fonteRotulos">
                  {puxando
                    ? "Solte o arquivo aqui"
                    : "Clique aqui ou arraste um arquivo para carregar"}
                </div>
              </>
            )}
          </div>
        </ImageUploadWithCrop>
      </div>
    );
  };

  // "Produtos em promoção" só é oferecido quando o catálogo tem a promoção
  // (ajuste de preço) ligada — senão o banner apontaria para uma lista vazia.
  const promocaoAtiva = Boolean(catalogo.ajuste_preco_ativo);

  const opcoesDestino: Array<{ tipo: BannerDestino["tipo"]; label: string }> = [
    { tipo: "nenhum", label: "Sem direcionamento" },
    { tipo: "categoria", label: "Para uma categoria" },
    { tipo: "tag", label: "Para uma tag de destaque" },
    { tipo: "variacao", label: "Para uma variação" },
    ...(promocaoAtiva
      ? [{ tipo: "promocao" as const, label: "Para produtos em promoção" }]
      : []),
  ];

  // Cada direcionamento precisa de algo para apontar. Sem isso, o rádio fica
  // bloqueado com um "i" explicando o porquê — em vez de deixar o cliente
  // selecionar para só então descobrir que está vazio. Retorna a mensagem do
  // "i" quando indisponível, ou null quando o destino tem dados.
  const indisponivelPara = (tipo: BannerDestino["tipo"]): string | null => {
    switch (tipo) {
      case "categoria":
        return carregouOpcoes && opcoesCategoria.length === 0
          ? "Os produtos deste catálogo não têm categorias. Cadastre categorias nos produtos para liberar este direcionamento."
          : null;
      case "tag":
        return carregouOpcoes && opcoes.tags.length === 0
          ? "Os produtos deste catálogo não têm tags de destaque. Cadastre tags de destaque nos produtos para liberar este direcionamento."
          : null;
      case "variacao":
        return carregouOpcoes && opcoes.variacoes.length === 0
          ? "Os produtos deste catálogo não têm variações. Cadastre variações nos produtos para liberar este direcionamento."
          : null;
      case "promocao":
        return temPromocao
          ? null
          : "Este catálogo não tem produtos em promoção. Ative um Ajuste de Preço que reduza o valor para o banner ter para onde direcionar.";
      default:
        return null;
    }
  };

  return (
    <BannersShowcaseContainer theme={theme}>
      <div className="banner-secao fonteSubtitulo">Banners</div>

      {cadastrados.length > 0 && (
        <TableMainContainer theme={theme} className="banner-tabela">
          <div className="tableContainer">
            <table>
              <thead>
                <tr>
                  {["Ordem", "Mídia", "Direcionamento", "Status"].map((coluna) => (
                    <th key={coluna}>
                      <div className="th-wrapper fonteTexto">{coluna}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={aoSoltar}
                >
                  <SortableContext
                    items={cadastrados.map(({ index }) => String(index))}
                    strategy={verticalListSortingStrategy}
                  >
                    {cadastrados.map(({ banner, index }) => (
                      <LinhaBanner
                        key={`banner-${index}`}
                        id={String(index)}
                        aberto={editando === index}
                        disabled={disabled}
                        capa={urlDaArte(banner.imagem_mobile ?? banner.imagem_desktop)}
                        destino={descreverDestino(banner.destino)}
                        ativo={banner.ativo}
                        onAbrir={() => abrirEdicao(index)}
                        onAlternarAtivo={() => alternarAtivo(index)}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
              </tbody>
            </table>
          </div>
        </TableMainContainer>
      )}

      {/* Divisor entre a lista já cadastrada e o formulário, como no figma. */}
      {cadastrados.length > 0 && <div className="banner-divisor" />}

      {erro && <div className="banner-erro fonteTexto">{erro}</div>}

      {editando === null ? (
        !disabled && noLimite && quantidade_permitida < 3 ? (
          <div className="banner-bloqueado">
            <LockedIcon
              mensagem="Adicione mais banners a partir do plano Essencial"
              width="3rem"
              redirect={true}
            />
          </div>
        ) : null
      ) : (
        bannerAberto && (
          <div className="banner-formulario">
            <div className="banner-grupo">
              <div className="banner-titulo fonteTexto">Imagens</div>
              <div className="banner-artes">
                {upload("imagem_desktop", DESKTOP, "Para computadores", "1400 x 175px")}
                {upload("imagem_mobile", MOBILE, "Para celulares", "640 x 320px")}
              </div>
            </div>

            <div className="banner-grupo">
              <div className="banner-titulo fonteTexto">Vincular destino</div>
              <div className="banner-subtitulo fonteRotulos">
                Ao clicar sobre o banner seu cliente será direcionado para a filtragem
                escolhida abaixo.
              </div>
            </div>

            <div className="banner-opcoes fonteTexto">
              {opcoesDestino.map((opcao) => {
                const indisponivel = indisponivelPara(opcao.tipo);
                const desabilitada = indisponivel !== null;
                return (
                  <label
                    className={`banner-opcao${desabilitada ? " desabilitada" : ""}`}
                    key={opcao.tipo}
                  >
                    <input
                      type="radio"
                      name="destino-banner"
                      checked={bannerAberto.destino.tipo === opcao.tipo}
                      disabled={desabilitada}
                      onChange={() => definirDestino(opcao.tipo)}
                    />
                    <span>{opcao.label}</span>
                    {desabilitada && (
                      <PopoverInfo
                        className="popover-direcionamento"
                        mensagem={indisponivel}
                        width="8"
                      />
                    )}
                  </label>
                );
              })}
            </div>

            {bannerAberto.destino.tipo === "categoria" && (
              <div className="banner-selects">
                <Select
                  label="Categoria"
                  value={String(
                    opcoesCategoria.findIndex((opcao) => {
                      const destino = bannerAberto.destino as {
                        categoria: string;
                        subcategoria?: string;
                      };
                      return (
                        opcao.categoria === destino.categoria &&
                        (opcao.subcategoria ?? "") === (destino.subcategoria ?? "")
                      );
                    })
                  )}
                  disabled={opcoesCategoria.length === 0}
                  placeholder={
                    opcoesCategoria.length === 0
                      ? "Nenhuma categoria nos produtos deste catálogo"
                      : "Selecione a categoria"
                  }
                  options={opcoesCategoria.map((opcao, index) => ({
                    name: opcao.label,
                    value: String(index),
                  }))}
                  onChange={(valor: string) => {
                    const opcao = opcoesCategoria[Number(valor)];
                    if (!opcao) return;
                    alterarBannerAberto({
                      destino: {
                        tipo: "categoria",
                        categoria: opcao.categoria,
                        ...(opcao.subcategoria ? { subcategoria: opcao.subcategoria } : {}),
                      },
                    });
                  }}
                />
              </div>
            )}

            {bannerAberto.destino.tipo === "tag" && (
              <div className="banner-selects">
                <Select
                  label="Tag de destaque"
                  value={bannerAberto.destino.tag}
                  disabled={opcoes.tags.length === 0}
                  placeholder={
                    opcoes.tags.length === 0
                      ? "Nenhuma tag nos produtos deste catálogo"
                      : "Selecione a tag"
                  }
                  options={opcoes.tags.map((tag) => ({ name: tag, value: tag }))}
                  onChange={(valor: string) =>
                    alterarBannerAberto({ destino: { tipo: "tag", tag: valor } })
                  }
                />
              </div>
            )}

            {bannerAberto.destino.tipo === "variacao" && (
              <div className="banner-selects">
                <Select
                  label="Variação"
                  value={bannerAberto.destino.variacao}
                  disabled={opcoes.variacoes.length === 0}
                  placeholder={
                    opcoes.variacoes.length === 0
                      ? "Nenhuma variação nos produtos deste catálogo"
                      : "Selecione a variação"
                  }
                  options={opcoes.variacoes.map((v) => ({ name: v, value: v }))}
                  onChange={(valor: string) =>
                    alterarBannerAberto({ destino: { tipo: "variacao", variacao: valor } })
                  }
                />
              </div>
            )}

            <div className="banner-acoes">
              <Button
                className="button fonteTexto"
                title="Salvar banner"
                width="100%"
                padding="1rem"
                onClick={concluirEdicao}
              />
            </div>
          </div>
        )
      )}
    </BannersShowcaseContainer>
  );
};

export default BannersShowcase;
